import { Transport, CaptureEvent } from './transport';
import { Logger } from '../utils/logger';
import { Storage } from './storage';

type StorageType = 'localStorage' | 'sessionStorage' | 'memory';

/** Maximum number of events to hold in the queue before dropping oldest. */
const MAX_QUEUE_SIZE = 1000;

/** Minimum interval (ms) between localStorage writes to avoid I/O thrash. */
const PERSIST_DEBOUNCE_MS = 1000;

export class EventQueue {
    private queue: CaptureEvent[] = [];
    private transport: Transport;
    private logger: Logger;
    private batchSize: number;
    private flushIntervalMs: number;
    private maxRetries: number;
    private flushTimer: ReturnType<typeof setInterval> | null = null;
    private isFlushing: boolean = false;
    private storage: Storage;
    private persistTimer: ReturnType<typeof setTimeout> | null = null;
    private persistDirty: boolean = false;

    // Bound handlers for proper cleanup
    private boundBeforeUnload: (() => void) | null = null;
    private boundPageHide: (() => void) | null = null;
    private boundVisibilityChange: (() => void) | null = null;

    constructor(
        transport: Transport,
        logger: Logger,
        batchSize: number = 10,
        flushIntervalMs: number = 5000,
        maxRetries: number = 3,
        storageType: StorageType = 'localStorage',
        storagePrefix: string = 'honch_queue_'
    ) {
        this.transport = transport;
        this.logger = logger;
        this.batchSize = batchSize;
        this.flushIntervalMs = flushIntervalMs;
        this.maxRetries = maxRetries;
        this.storage = new Storage(storageType, storagePrefix);

        // Load any persisted events from previous session
        this.loadPersistedQueue();

        // Flush on page unload — store references so we can remove them later
        if (typeof window !== 'undefined') {
            this.boundBeforeUnload = () => this.flushSync();
            this.boundPageHide = () => this.flushSync();
            this.boundVisibilityChange = () => {
                if (document.visibilityState === 'hidden') {
                    this.flushSync();
                }
            };
            window.addEventListener('beforeunload', this.boundBeforeUnload);
            window.addEventListener('pagehide', this.boundPageHide);
            document.addEventListener('visibilitychange', this.boundVisibilityChange);
        }
    }

    /**
     * Start the automatic flush timer
     */
    start(): void {
        if (this.flushTimer) return;

        this.flushTimer = setInterval(() => {
            this.flush();
        }, this.flushIntervalMs);

        this.logger.debug('Queue started', { intervalMs: this.flushIntervalMs });
    }

    /**
     * Stop the automatic flush timer and remove event listeners.
     */
    stop(): void {
        if (this.flushTimer) {
            clearInterval(this.flushTimer);
            this.flushTimer = null;
        }
        if (this.persistTimer) {
            clearTimeout(this.persistTimer);
            this.persistTimer = null;
        }

        // Remove unload listeners to prevent leaks
        if (this.boundBeforeUnload) {
            window.removeEventListener('beforeunload', this.boundBeforeUnload);
            this.boundBeforeUnload = null;
        }
        if (this.boundPageHide) {
            window.removeEventListener('pagehide', this.boundPageHide);
            this.boundPageHide = null;
        }
        if (this.boundVisibilityChange) {
            document.removeEventListener('visibilitychange', this.boundVisibilityChange);
            this.boundVisibilityChange = null;
        }

        // Final persist of any pending events
        if (this.persistDirty) {
            this.persistQueueNow();
        }

        this.logger.debug('Queue stopped');
    }

    /**
     * Add an event to the queue
     */
    enqueue(event: CaptureEvent): void {
        this.queue.push(event);

        // Enforce max queue size — drop oldest events to prevent unbounded growth
        if (this.queue.length > MAX_QUEUE_SIZE) {
            const dropped = this.queue.length - MAX_QUEUE_SIZE;
            this.queue = this.queue.slice(dropped);
            this.logger.warn('Queue overflow, dropped oldest events', { dropped });
        }

        this.persistQueue();

        this.logger.debug('Event enqueued', { event: event.event, queueSize: this.queue.length });

        // Flush if batch size reached
        if (this.queue.length >= this.batchSize) {
            this.flush();
        }
    }

    /**
     * Flush all events in the queue (async)
     */
    async flush(): Promise<void> {
        if (this.isFlushing || this.queue.length === 0) {
            return;
        }

        this.isFlushing = true;

        // Take all events from queue
        const events = [...this.queue];
        this.queue = [];
        this.persistQueueNow();

        this.logger.debug('Flushing events', { count: events.length });

        try {
            await this.sendWithRetry(events);
            this.logger.debug('Flush successful', { count: events.length });
        } catch (error) {
            this.logger.error('Flush failed, re-queueing events', { error, count: events.length });
            // Re-queue failed events at the front
            this.queue = [...events, ...this.queue];
            this.persistQueueNow();
        } finally {
            this.isFlushing = false;
        }
    }

    /**
     * Flush synchronously using sendBeacon (for page unload)
     */
    private flushSync(): void {
        if (this.queue.length === 0) return;

        const events = [...this.queue];
        this.queue = [];
        this.persistQueueNow();

        this.logger.debug('Sync flushing events', { count: events.length });
        this.transport.sendBeacon(events);
    }

    /**
     * Send events with exponential backoff retry
     */
    private async sendWithRetry(events: CaptureEvent[]): Promise<void> {
        let lastError: Error | null = null;

        for (let attempt = 0; attempt < this.maxRetries; attempt++) {
            try {
                await this.transport.sendBatch(events);
                return;
            } catch (error) {
                lastError = error as Error;
                const delay = Math.pow(2, attempt) * 1000 + Math.random() * 1000;
                this.logger.warn(`Send failed, retrying in ${delay}ms`, { attempt, error });
                await this.sleep(delay);
            }
        }

        throw lastError || new Error('Send failed after retries');
    }

    private sleep(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    /**
     * Debounced persistence — avoids thrashing localStorage on high-frequency capture.
     */
    private persistQueue(): void {
        this.persistDirty = true;
        if (this.persistTimer) return;

        this.persistTimer = setTimeout(() => {
            this.persistTimer = null;
            this.persistQueueNow();
        }, PERSIST_DEBOUNCE_MS);
    }

    private persistQueueNow(): void {
        this.persistDirty = false;
        try {
            this.storage.set('events', this.queue);
        } catch (e) {
            // Storage might be full or unavailable
            this.logger.warn('Failed to persist queue', { error: e });
        }
    }

    private loadPersistedQueue(): void {
        try {
            const persisted = this.storage.get('events');
            if (Array.isArray(persisted) && persisted.length > 0) {
                this.queue = persisted;
                this.logger.debug('Loaded persisted events', { count: persisted.length });
            }
        } catch (e) {
            this.logger.warn('Failed to load persisted queue', { error: e });
        }
    }
}
